import { getActiveAuthSession } from '@modeling/account';
import { createClient } from '@modeling/git-file-viewer';
import { WORKFLOW_PROFILES_DIR } from '../config/schemaGit';
import type { SchemaProfile } from '../types/schemaProfile';
import { isGitCredentialNotConfiguredError, isGitPathNotFoundError } from '../utils/gitErrors';
import { resolveGitApiBaseUrl } from './env';
import { pydcApi } from './pydcApi';
import type { SchemaGitConnection } from './schemaProfileApi';

function getToken(): string {
  return getActiveAuthSession()?.token ?? '';
}

export type ListWorkflowProfilesResult =
  | { ok: true; profiles: SchemaProfile[] }
  | { ok: false; reason: 'credential_required' };

export type WorkflowApplyAction = 'create' | 'update' | 'noop' | 'error';

export interface WorkflowApplyTenantRow {
  tenantId: string;
  tenantCode?: string;
  tenantName?: string | null;
  path?: string;
  code?: string;
  action: WorkflowApplyAction;
  id?: string | null;
  changes?: Record<string, unknown>;
  error?: string | null;
}

export interface WorkflowApplyResult {
  dryRun: boolean;
  schemaProfile: string;
  repoUrl: string;
  ref: string;
  codes: string[];
  declarationCount: number;
  tenantCount: number;
  tenants: Array<{ id: string; code: string; name?: string | null }>;
  results: WorkflowApplyTenantRow[];
}

export interface WorkflowApplyParams {
  repoUrl: string;
  schemaProfile: string;
  branch: string;
  dryRun: boolean;
}

/** 从 Git 仓库 workflow/ 目录发现 profile（缺目录当空列表） */
export async function listWorkflowProfilesFromGit(
  conn: SchemaGitConnection,
): Promise<ListWorkflowProfilesResult> {
  const baseURL = resolveGitApiBaseUrl();
  const { repoUrl, branch } = conn;

  if (!baseURL) {
    throw new Error('未配置 REACT_APP_GIT_API_BASE_URL');
  }
  if (!repoUrl) {
    throw new Error('请填写 Git 仓库 URL');
  }

  try {
    const client = createClient(baseURL, getToken);
    const tree = await client.getTree(repoUrl, branch, WORKFLOW_PROFILES_DIR);

    const profiles = tree.items
      .filter((item) => item.type === 'directory')
      .map((item) => ({
        id: item.name,
        name: item.name,
        createdAt: '',
        updatedAt: '',
      }))
      .sort((a, b) => a.id.localeCompare(b.id));

    return { ok: true, profiles };
  } catch (err) {
    if (isGitCredentialNotConfiguredError(err)) {
      return { ok: false, reason: 'credential_required' };
    }
    if (isGitPathNotFoundError(err)) {
      return { ok: true, profiles: [] };
    }
    throw err;
  }
}

/** 调用 pydc 把 workflow/{profile} 声明同步到当前登录租户 */
export async function applyWorkflowProfile(params: WorkflowApplyParams): Promise<WorkflowApplyResult> {
  const response = await pydcApi.post<WorkflowApplyResult>('/admin/workflow/profiles/apply', {
    repoUrl: params.repoUrl,
    schemaProfile: params.schemaProfile,
    branch: params.branch,
    dryRun: params.dryRun,
  });
  return response.data;
}
